import { useQuery, queryOptions } from '@tanstack/react-query'
import { fetchFeatureFlagsWithParams } from '@/features/shared/services/FeatureFlags/fetchers'
import { type StoreParams, useStoreParams } from '@/features/shared/utils/dataFetching/storeParams'
import { useSession } from '@/features/authentication/utils/authentication/hooks/useSession'
import { featureFlagsQueryOptions } from '@/features/shared/services/FeatureFlags/queryOptions'

export type FeatureFlagsKey = readonly ['featureFlags', number]

export const useQueryFeatureFlags = ({ enabled = true } = {}) => {
  const { isFullyAuthenticated, userId } = useSession()

  return useQuery({
    ...featureFlagsQueryOptions({ userId }),
    placeholderData: (previousData) => previousData,
    enabled: enabled && (isFullyAuthenticated ? Boolean(userId) : true),
  })
}

export type FeatureFlagsWithParamsKey = readonly ['featureFlagsWithParams', number, StoreParams]

// you only want to use this query if you need to access snap_ebt
// and snap_ebt_post_order_modification feature flags.
const featureFlagsWithParamsQueryOptions = ({
  userId,
  storeParams,
}: {
  userId: number
  storeParams: StoreParams
}) =>
  queryOptions({
    queryKey: ['featureFlagsWithParams', userId, storeParams] as const,
    queryFn: fetchFeatureFlagsWithParams,
    // we don't want featureflags to refetch during a user session, so that we maintain a consistent user experience during a session
    staleTime: Infinity,
  })
export const useQueryFeatureFlagsWithParams = () => {
  const storeParams = useStoreParams()
  const { userId } = useSession()

  return useQuery({
    ...featureFlagsWithParamsQueryOptions({ userId, storeParams }),
    placeholderData: (previousData) => previousData,
    enabled: Boolean(storeParams.store_location_id),
  })
}
